import React, { useState, useEffect, useCallback } from 'react';
import { throttle } from 'lodash-es';
import { State } from 'state';
import { useDispatch, useSelector } from 'react-redux';
import SearchUser from '../search';
import { clearUserSuggestions } from '../../../state/actions';
import { ModerationSearchType, ContentType } from '../../../state/types';
import findModerationUser from '../../../state/actions/find-user';
import { track, useTracking } from 'react-tracking';

const getModerationSearchTypes = (contentType: ContentType) =>
  [
    ModerationSearchType.USERNAME,
    ModerationSearchType.EMAIL,
    ModerationSearchType.MESSAGE_ID,
    contentType === 'COMMENTS' && ModerationSearchType.TEXT
  ].filter(Boolean);

const PLACEHOLDERS = {
  [ModerationSearchType.USERNAME]: 'Type username...',
  [ModerationSearchType.EMAIL]: 'Enter user email (exact match)',
  [ModerationSearchType.TEXT]: 'Type text...',
  [ModerationSearchType.MESSAGE_ID]: 'Type Message ID...'
};

const THROTTLE_SEARCH_MS = 800;

interface Props {
  isExpanded: boolean;
  searchValue: string;
  searchType: ModerationSearchType;
  contentType: ContentType;

  // Changes isExpanded prop
  openSearchBar: () => void;
  closeSearchBar: (withReset?: boolean) => void;

  // Updates search moderation state according user's input.
  search: (props: { type: ModerationSearchType; value: string; userId?: string }) => void;
  resetAllFilters: () => void;
}

const ModerationSearch: React.FC<Props> = ({
  isExpanded,
  searchValue,
  searchType = ModerationSearchType.USERNAME,
  openSearchBar,
  closeSearchBar,
  search,
  resetAllFilters,
  contentType
}) => {
  const [value, setValue] = useState<string>(searchValue);
  const [type, setType] = useState<ModerationSearchType>(searchType);
  const tracking = useTracking();

  const dispatch = useDispatch();
  const suggestions = useSelector((state: State) => state?.moderation?.search?.suggestions);

  const onSearchTypeChange = (value: string, type: ModerationSearchType) => {
    tracking.trackEvent({ eventName: 'moderation search type clicked', search_type: type });
    setType(type);
  };

  const onSearchValueChange = (value: string, type: ModerationSearchType) => {
    setValue(value);

    // Search suggestions for searched value
    if (type === ModerationSearchType.USERNAME) {
      fetchUserSuggestions(value);
    }
  };

  const cancelSearch = () => {
    setType(ModerationSearchType.USERNAME);
    setValue('');

    // Reset all filters only in case
    // the search request has already done
    if (searchValue) {
      resetAllFilters();
    }
  };

  const onSuggestionClick = (userId: string, username: string) => {
    tracking.trackEvent({
      eventName: 'moderation search results clicked',
      click_type: 'suggested'
    });
    setValue(username);
    search({ type, value: username, userId });
    closeSearchBar();
  };

  const clearSuggestions = () => {
    dispatch(clearUserSuggestions());
  };

  const fetchUserSuggestions = useCallback<(value: string) => void>(
    throttle((value: string) => {
      if (value.length > 1) {
        dispatch(findModerationUser({ search: value, type }));
      }
    }, THROTTLE_SEARCH_MS),
    []
  );

  useEffect(() => {
    // In case global state searchValue has changed (by url params)
    // we are setting the right value on local state.
    if (searchValue && value.length === 0) {
      setValue(searchValue);
    } else if (searchValue.length === 0 && value.length > 0) {
      setValue(searchValue);
    }
  }, [searchValue]);

  return (
    <SearchUser
      isExpanded={isExpanded}
      currentSearchType={type}
      searchValue={value}
      searchTypes={getModerationSearchTypes(contentType)}
      onCloseSearch={closeSearchBar}
      onOpenSearch={openSearchBar}
      onSearch={search}
      onSearchTypeChange={onSearchTypeChange}
      onSearchValueChange={onSearchValueChange}
      cancelSearch={cancelSearch}
      placeholder={PLACEHOLDERS[type]}
      suggestions={suggestions}
      onSuggestionClick={onSuggestionClick}
      clearSuggestions={clearSuggestions}
    />
  );
};

export default track(props => ({ search_type: props.searchType, search_value: props.searchValue }))(
  ModerationSearch
);
